import React, { FC, useEffect, useState } from 'react'
import { Button, Spinner } from '@hospitalrun/components'
import { useTranslation } from 'react-i18next'
import {
  MAX_AUTOMATIC_ATTEMPTS,
  automaticAttempts,
  checkForServiceWorkerUpdate,
  clearAutomaticAttempts,
  currentVersion,
  fetchDeployedVersion,
  isBelowMinimum,
  reloadIntoLatestVersion,
} from './appVersion'
import './ForceUpdate.scss'

const CHECK_INTERVAL_MS = 5 * 60 * 1000
/** Focus and visibility changes come in bursts; one check per minute is plenty. */
const MIN_CHECK_GAP_MS = 60 * 1000
/** A reload that hasn't happened by now isn't going to. */
const RELOAD_GIVE_UP_MS = 20000

type Phase = 'current' | 'updating' | 'manual'

/**
 * Blocks a build older than the deployment's minimum version (see scripts/forceUpdate.js).
 *
 * It updates automatically — drops the service worker and caches, then reloads — and only when that
 * keeps landing on the old build does it explain how to update by hand. A whole screen rather than a
 * dismissible prompt: the old build is no longer supported, so there is nothing to continue with.
 *
 * Normal releases stay out of its way; VersionVerif offers those as an optional reload.
 */
const ForceUpdate: FC = () => {
  const { t } = useTranslation()
  const [phase, setPhase] = useState<Phase>('current')
  const [requiredVersion, setRequiredVersion] = useState('')

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production' || !currentVersion) {
      return undefined
    }

    let done = false
    let lastCheck = 0

    const check = async () => {
      if (done || Date.now() - lastCheck < MIN_CHECK_GAP_MS) {
        return
      }
      lastCheck = Date.now()

      const deployed = await fetchDeployedVersion()
      if (done || !deployed) {
        return
      }

      if (!isBelowMinimum(currentVersion, deployed.minVersion)) {
        clearAutomaticAttempts()
        if (deployed.version !== currentVersion) {
          // Long-lived tabs never navigate, so the browser wouldn't look for the new worker on its own.
          checkForServiceWorkerUpdate()
        }
        return
      }

      done = true
      setRequiredVersion(deployed.minVersion)
      if (automaticAttempts(deployed.minVersion) >= MAX_AUTOMATIC_ATTEMPTS) {
        setPhase('manual')
      } else {
        setPhase('updating')
        reloadIntoLatestVersion(deployed.minVersion)
      }
    }

    const onFocus = () => {
      if (document.visibilityState === 'visible') {
        check()
      }
    }

    check()
    const timer = window.setInterval(check, CHECK_INTERVAL_MS)
    document.addEventListener('visibilitychange', onFocus)
    window.addEventListener('focus', onFocus)

    return () => {
      done = true
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', onFocus)
      window.removeEventListener('focus', onFocus)
    }
  }, [])

  useEffect(() => {
    if (phase !== 'updating') {
      return undefined
    }
    const timer = window.setTimeout(() => setPhase('manual'), RELOAD_GIVE_UP_MS)
    return () => window.clearTimeout(timer)
  }, [phase])

  if (phase === 'current') {
    return null
  }

  const tryAgain = () => {
    setPhase('updating')
    reloadIntoLatestVersion(requiredVersion)
  }

  if (phase === 'updating') {
    return (
      <div className="forceUpdate" role="alertdialog" aria-modal="true" aria-busy="true">
        <div className="forceUpdate__panel forceUpdate__panel--center">
          <Spinner color="blue" loading size={[10, 25]} type="ScaleLoader" />
          <h1 className="forceUpdate__title">{t('forceUpdate.updatingTitle', 'Updating Mediora')}</h1>
          <p className="forceUpdate__text">
            {t(
              'forceUpdate.updatingText',
              'A required update is being installed. The page will reload by itself in a moment.',
            )}
          </p>
        </div>
      </div>
    )
  }

  const { supportPhone, supportEmail } = (window as any).config || {}

  return (
    <div className="forceUpdate" role="alertdialog" aria-modal="true">
      <div className="forceUpdate__panel">
        <h1 className="forceUpdate__title">{t('forceUpdate.manualTitle', 'Please update Mediora')}</h1>
        <p className="forceUpdate__text">
          {t(
            'forceUpdate.manualText',
            'This browser is still running an old version of Mediora that is no longer supported, and the automatic update did not take effect. Please update it yourself:',
          )}
        </p>

        <ol className="forceUpdate__steps">
          <li>
            {t(
              'forceUpdate.stepHardReload',
              'Press Ctrl + Shift + R (on a Mac: Cmd + Shift + R) to reload the page without the cache.',
            )}
          </li>
          <li>
            {t(
              'forceUpdate.stepCloseTabs',
              'If this screen comes back, close every Mediora tab and window, then open Mediora again.',
            )}
          </li>
          <li>
            {t(
              'forceUpdate.stepClearData',
              "Still here? Clear this site's stored data: click the icon to the left of the web address, choose Site settings, then Delete data (Chrome and Edge). Then open Mediora again.",
            )}
          </li>
          {(supportPhone || supportEmail) && (
            <li>
              {t('forceUpdate.stepSupport', 'If none of this helps, contact support:')}{' '}
              {[supportPhone, supportEmail].filter(Boolean).join(' · ')}
            </li>
          )}
        </ol>

        <div className="forceUpdate__meta">
          {t('forceUpdate.versions', 'Your version: {{current}} · Required: {{required}} or newer', {
            current: currentVersion,
            required: requiredVersion,
          })}
        </div>

        <div className="forceUpdate__actions">
          <Button color="primary" onClick={tryAgain}>
            {t('forceUpdate.tryAgain', 'Try again')}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default ForceUpdate
